import React, { useState, useEffect } from 'react';
import { Badge, IconButton, Popover, Link } from '@material-ui/core';
import {
  ArrowBack as ArrowBackIcon,
  NotificationsOutlined as NotificationsIcon,
  GetApp as DownloadIcon,
  ErrorOutline as ErrorIcon,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { userService } from 'views/Users/_services';

import api from 'utils/api';
import { blobToFileDownload, getCurrentAccount } from 'utils/helpers';
import NotificationModal from './NotificationModal';

const NotificationMenu = () => {
  const { t } = useTranslation();
  const [hasNotifications, setHasNotifications] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [records, setRecords] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchRecords = async () => {
    try {
      const { _id } = getCurrentAccount();

      api.useGlobalLoader = false;
      const { data } = await api.get(`/accounts/${_id}/recent-notifications`, { hideGlobalLoader: true });
      setRecords(data);

      setHasNotifications(data.length > 0);
      handleAutoshow(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchRecords();

    // Set up interval to call fetchData every 10 seconds
    const intervalId = setInterval(fetchRecords, 10000);

    const handleFetchAndOpen = async () => {
      await fetchRecords();
      setTimeout(() => {
        const btn = document.querySelector('#notification-button');
        if (btn) {
          setAnchorEl(btn);
        }
      }, 100);
    };

    window.addEventListener('fetchAndOpenNotifications', handleFetchAndOpen);

    // Cleanup interval on component unmount
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('fetchAndOpenNotifications', handleFetchAndOpen);
    };
  }, []);

  const handleAutoshow = (data) => {
    const alreadyNotifiedRecords = JSON.parse(sessionStorage.getItem('alreadyShownNotifications')) || [];
    const newReadyRecords = data.filter((r) => ['ready', 'completed', 'error', 'failed'].includes(r.status) 
      && (!alreadyNotifiedRecords.includes(r._id))).map((r) => r._id);

    if (newReadyRecords.length > 0) {
      sessionStorage.setItem('alreadyShownNotifications', JSON.stringify([...alreadyNotifiedRecords, ...newReadyRecords]));
      document.querySelector('#notification-button').click();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onShowAllClick = async (e) => {
    setIsModalOpen(true);
    setAnchorEl(null);
  }

  const handleModalClose = () => {
    setIsModalOpen(false);
  }

  const renderStatusIndicator = (status) => {
    const className = `notification-status ${status}`;
    const textMap = {
      processing: t('Ruošiama'),
      ready: t('Atsisiųsti'),
      completed: t('Atidaryti'),
      failed: t('Klaida'),
      deleted: t('Pašalinta'),
    };

    return (
      <div className={className}>{textMap[status] || '-'}</div>
    )
  };

  const handleNotificationClick = async (record) => {
    // if notification has a file -> download
    if (record.file) {
      try {
        const { data } = await api.get(`/notifications/${record._id}/download`);
        const { fileName, contentType, content } = data;

        var bytes = new Uint8Array(content);
        const blob = new Blob([bytes], { type: contentType });

        blobToFileDownload(fileName, blob);
      } catch (err) {
        console.error(err);
      }
    }

    // if notification has a link -> navigate
    if (record.link && !record?.file) {
      window.location.href = record.link;
    }

    // mark notification as opened
    userService.setNotificationOpened(getCurrentAccount()._id, record._id);
  }

  const handleOpenAll = async () => {
    if (!window.confirm(t('Ar tikrai norite pašalinti visus pranešimus?'))) {
      return;
    }
    try {
      await api.post(`/notifications/open-all`);
      setRecords([]);
      setAnchorEl(null);
      toast(t('Pranešimai pašalinti'), 'success');
    } catch (err) {
      console.error(err);
    }
  }

  const renderNotificationIcon = (notification) => {
    if (!!notification.file && notification.status === 'ready') {
      return <DownloadIcon style={{ marginTop: '3px' }} />;
    }
    switch (notification.status) {
      case 'error':
        return <ErrorIcon style={{ color: 'red' }} />;
      case 'ready':
        return <NotificationsIcon style={{ marginTop: '3px' }} />;
      default:
        return;
    }
  }

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <div>
      <IconButton id="notification-button" className="" onClick={handleClick} >
        <Badge
          badgeContent={hasNotifications ? 1 : 0}
          color="primary"
          variant="dot"
          overlap="rectangular"
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div className="notification-wrap" style={{ padding: '10px', width: '300px', maxHeight: '55vh' }}>
          <div className="notification-header">
            <h5>{t('Pranešimai')}</h5>

            <div style={{ display: 'flex', position: 'absolute', right: '5px'}}>
              <a href="#" onClick={onShowAllClick} style={{ marginRight: '8px' }}>{t('Rodyti visus')}</a>
              <a href="#" onClick={() => handleOpenAll()} >{t('Pašalinti visus')}</a>
            </div>
          </div>

          <ul className="notification-list" style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
            {records.map((record) => (
                <li className={`notification-item-${record.status}`} key={record._id} onClick={() => handleNotificationClick(record)}>
                  <div className="col-left" style={{ width: '80%' }}>
                    <span className="notification-link">
                      <div className="notification-ts">
                        {moment(record.createdAt).format('YYYY-MM-DD HH:mm')}
                      </div>

                      {record.content}
                    </span>
                  </div>
                  <div className="col-right" style={{ width: '20%' }}>
                    {record.formNumber && <div className="form-number">{record.formNumber}</div>}
                    {renderNotificationIcon(record)}
                  </div>

                  <div className="notification-status-wrap">
                    {record.status != 'new' && renderStatusIndicator(record.status)}
                  </div>
                </li>
            ))}
          </ul>
        </div>
      </Popover>
      <NotificationModal accountId={getCurrentAccount()._id} isOpen={isModalOpen} onClose={handleModalClose} />
    </div>
  );
}

export default NotificationMenu;